/**
 * shell/useViewport.ts —— **断点解算 ＋ 键盘弹起适配**（壳规范 §1／§1bis）。
 *
 * 依据：三端断点（≥1200 桌面／768–1199 平板／<768 手机；真值单源 `shell.decl.yaml`，
 *      102 真源 `layout/capabilities.js::BREAKPOINTS` 768/1200）｜§1bis（手机＝单栏＋抽屉／sheet；
 *      输入区固定底部＋`env(safe-area-inset-bottom)`＋**`visualViewport` 键盘适配**）。
 * 纪律：断点真值**取自 `shell.decl.yaml`**（`resolveBreakpoint`），本件不写断点数字；
 *      键盘位移只写 `--xg-vv-offset`（呈现层），不参与业务；键盘判定式＝102 `utils/keyboard.js`
 *      （主信号 offsetTop>0 ∨ 副信号基线缩差>150px，两信号取或；禁 ratio 阈值）。
 */
import { useEffect, useState } from "react";
import { resolveBreakpoint, type BreakDecl } from "./shellDecl";

export interface ViewportState {
  width: number;
  orientation: "portrait" | "landscape";
  break: BreakDecl;
  /** 是否手机档（<768；单栏＋抽屉／sheet） */
  phone: boolean;
  /** 键盘弹起位移（px；无键盘恒 0） */
  keyboardOffset: number;
}

/** 纯函数测量（可单测；不依赖 React） */
export function measureViewport(width: number, height: number, keyboardOffset = 0): ViewportState {
  const orientation: ViewportState["orientation"] = width > height ? "landscape" : "portrait";
  const breakDecl = resolveBreakpoint(width, orientation);
  return {
    width,
    orientation,
    break: breakDecl,
    phone: breakDecl.sidebar === "drawer",
    keyboardOffset: Math.max(0, Math.round(keyboardOffset)),
  };
}

/** 键盘判定副信号阈值（真源＝102 utils/keyboard.js:47：基线缩差 >150px，绝对值非 ratio） */
export const KEYBOARD_SHRINK_PX = 150;

function readViewport(): ViewportState {
  if (typeof window === "undefined") return measureViewport(1024, 768);
  const vv = (window as unknown as { visualViewport?: VisualViewport }).visualViewport;
  const width = Math.round(vv?.width ?? window.innerWidth ?? 1024);
  const height = Math.round(vv?.height ?? window.innerHeight ?? 768);
  const layoutHeight = window.innerHeight ?? height;
  // 102 keyboard.js 判定式（逐字语义）：主信号＝offsetTop>0（视口被推起）；
  // 副信号＝基线缩差>150px。两信号取或；禁 ratio 阈值写法。
  const offsetTop = Math.round(vv?.offsetTop ?? 0);
  const shrink = Math.max(0, layoutHeight - height);
  const keyboardOffset = offsetTop > 0 || shrink > KEYBOARD_SHRINK_PX ? Math.max(shrink, offsetTop) : 0;
  return measureViewport(width, height, keyboardOffset);
}

/** 视口状态（resize／orientationchange／visualViewport 三源合流；写 `--xg-vv-offset`） */
export function useViewport(): ViewportState {
  const [state, setState] = useState<ViewportState>(readViewport);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const sync = (): void => {
      const next = readViewport();
      setState(next);
      try {
        document.documentElement.style.setProperty("--xg-vv-offset", next.keyboardOffset + "px");
      } catch {
        // 无 document（非浏览器环境）⇒ 只保状态，不写呈现层
      }
    };
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    const vv = (window as unknown as { visualViewport?: VisualViewport }).visualViewport;
    vv?.addEventListener?.("resize", sync);
    vv?.addEventListener?.("scroll", sync);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
      vv?.removeEventListener?.("resize", sync);
      vv?.removeEventListener?.("scroll", sync);
    };
  }, []);

  return state;
}
